import React, { useMemo } from 'react';
import { Bookmark, Hash, Link2 } from 'lucide-react';
import SourcesCitedBlock from './SourcesCitedBlock.jsx';
import { SOCIAL_CHANNELS } from '../../constants/channels.js';

const PIN_COLOR =
  (SOCIAL_CHANNELS.find((c) => c.id === 'pinterest') || {}).color || '#E60023';

const qaColor = (score) =>
  score >= 70 ? '#10B981' : score >= 50 ? '#F59E0B' : '#EF4444';

const renderTag = (raw) => {
  const s = String(raw || '').trim();
  if (!s) return '';
  return s.startsWith('#') ? s : `#${s}`;
};

// Per docs/frontend/pinterest-post-rendering.md §6.
// Threshold based on metadata.character_count = len(full_text).
const budgetTier = (count) => {
  const n = Number.isFinite(count) ? count : 0;
  if (n > 500) return { label: 'Oltre limite Pinterest (≤500)', color: '#EF4444', fillRatio: 1 };
  if (n > 450) return { label: 'Vicino al limite hard',          color: '#F97316', fillRatio: n / 500 };
  if (n > 300) return { label: 'Sopra lo sweet spot',             color: '#F59E0B', fillRatio: n / 500 };
  return            { label: 'Nello sweet spot',                  color: '#10B981', fillRatio: n / 500 };
};

const SectionLabel = ({ children, color, t }) => (
  <div
    style={{
      fontSize: '10px',
      fontFamily: "'JetBrains Mono', monospace",
      color: color || t.textMuted || '#7a7a8e',
      letterSpacing: '1px',
      textTransform: 'uppercase',
      fontWeight: 600,
      marginBottom: '8px',
    }}
  >
    {children}
  </div>
);

const PinterestResultEntry = ({
  entry,
  copiedId,
  onCopy,
  w = (o) => `rgba(255,255,255,${o})`,
  t = {},
}) => {
  const r = entry.results || {};
  const c = useMemo(() => r.content || {}, [r.content]);
  const color = PIN_COLOR;
  const eId = entry.id || 'pinterest';

  const qaScore = r.metadata?.qa_score ?? r.metadata?.style_compliance ?? null;

  const pinTitle = (c.pin_title || '').trim();
  const pinDescription = (c.pin_description || '').trim();
  const boardSuggestion = (c.board_suggestion || '').trim();
  const hashtags = Array.isArray(c.hashtags) ? c.hashtags.filter(Boolean) : [];
  const ctaUrl = (c.cta_url || '').trim();

  const charCount = Number.isFinite(r.metadata?.character_count)
    ? r.metadata.character_count
    : (c.full_text || '').length;

  const tier = budgetTier(charCount);

  return (
    <div>
      {/* Region 1 — QA Score bar */}
      {qaScore != null && (
        <div style={{ marginBottom: '18px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '6px',
            }}
          >
            <SectionLabel t={t}>QA Score</SectionLabel>
            <span
              style={{
                fontSize: '13px',
                fontWeight: 700,
                color: qaColor(qaScore),
                fontFamily: "'JetBrains Mono', monospace",
              }}
            >
              {qaScore}/100
            </span>
          </div>
          <div
            style={{
              height: '6px',
              borderRadius: '4px',
              background: w(0.06),
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${Math.min(100, Math.max(0, qaScore))}%`,
                height: '100%',
                background: qaColor(qaScore),
                transition: 'width 0.4s ease',
              }}
            />
          </div>
        </div>
      )}

      {/* Region 3 — Pin title (bold, no label) */}
      {pinTitle && (
        <div style={{ marginBottom: '12px' }}>
          <div
            style={{
              fontSize: '17px',
              fontWeight: 700,
              color: t.text,
              lineHeight: 1.35,
            }}
          >
            {pinTitle}
          </div>
        </div>
      )}

      {/* Region 4 — Pin description (no label, flat paragraph) */}
      {pinDescription && (
        <div style={{ marginBottom: '12px' }}>
          <p
            style={{
              margin: 0,
              fontSize: '14px',
              lineHeight: 1.7,
              color: t.text,
              whiteSpace: 'pre-wrap',
            }}
          >
            {pinDescription}
          </p>
        </div>
      )}

      {/* Region 5 — Board recommendation */}
      {boardSuggestion && (
        <div
          style={{
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexWrap: 'wrap',
            fontSize: '13px',
            color: t.textMuted || '#7a7a8e',
          }}
        >
          <Bookmark size={14} color={t.textMuted || '#7a7a8e'} />
          <span>Board consigliato:</span>
          <span style={{ fontWeight: 700, color: t.text }}>{boardSuggestion}</span>
        </div>
      )}

      {/* Region 6 — Hashtag chips (0-3 per spec) */}
      {hashtags.length > 0 && (
        <div style={{ marginBottom: '14px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '8px',
            }}
          >
            <Hash size={12} color={color} />
            <SectionLabel color={color} t={t}>
              Hashtag ({hashtags.length})
            </SectionLabel>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {hashtags.map((tag, i) => (
              <span
                key={i}
                style={{
                  padding: '3px 10px',
                  borderRadius: '6px',
                  background: w(0.04),
                  border: `1px solid ${w(0.06)}`,
                  color: t.text,
                  fontSize: '12px',
                  fontWeight: 500,
                }}
              >
                {renderTag(tag)}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Region 7 — Destination URL (cta_url) */}
      {ctaUrl && (
        <div
          style={{
            marginBottom: '14px',
            padding: '8px 12px',
            background: w(0.02),
            border: `1px dashed ${color}40`,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '12.5px',
            color: t.textMuted || '#7a7a8e',
            flexWrap: 'wrap',
          }}
          title="URL di destinazione del pin"
        >
          <Link2 size={13} color={color} />
          <span style={{ fontWeight: 600, color }}>Destinazione:</span>
          <a
            href={ctaUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: t.text,
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '11.5px',
              wordBreak: 'break-all',
              textDecoration: 'none',
            }}
          >
            {ctaUrl}
          </a>
        </div>
      )}

      {/* Region 8 — Character-count budget bar */}
      {charCount > 0 && (
        <div style={{ marginBottom: '14px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '6px',
            }}
          >
            <SectionLabel t={t}>Budget pin</SectionLabel>
            <span
              style={{
                fontSize: '12px',
                fontFamily: "'JetBrains Mono', monospace",
                color: tier.color,
                fontWeight: 700,
              }}
            >
              {charCount}/500 — {tier.label}
            </span>
          </div>
          <div
            style={{
              height: '6px',
              borderRadius: '4px',
              background: w(0.06),
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${Math.min(100, Math.max(0, tier.fillRatio * 100))}%`,
                height: '100%',
                background: tier.color,
                transition: 'width 0.4s ease',
              }}
            />
          </div>
        </div>
      )}

      {/* Region 10 — Sources cited */}
      <SourcesCitedBlock
        sources={r.sources?.cited}
        copiedId={copiedId}
        onCopy={onCopy}
        entryId={eId}
        w={w}
        t={t}
      />
    </div>
  );
};

export default PinterestResultEntry;
